import { useEffect, useRef, useState } from "react";
import { PrintData } from "../../../DirectBilling/Edit/exports";
import {
  ProcessingCode,
  TestType,
  TestTypeInterface,
} from "../../../DirectBilling/Edit/types/TestTypes.interface";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { RxStoreContext } from "../../../../../../stores/Rx/rx.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { useReactToPrint } from "react-to-print";
import {
  UpdateInsuranceTransactionDto,
  BillPdfBatchItemDtoPdfTypeEnum,
  ExportBillingData,
  LineItemDto,
} from "../../../../../../api-client";
import { useBatchPrintPdf } from "../../../../../../hooks/useBatchPrintPdf";
import { extractDocIdsFromBatchResults } from "../../../../../../utils/billing/documentDownload";

interface UseLineItemCorrectionEditProps {
  bills: ExportBillingData[];
  setBills: (bills: ExportBillingData[] | ((prev: ExportBillingData[]) => ExportBillingData[])) => void;
  lineItemSelections: Record<string, LineItemDto[]>;
  onFullScreenDialogClose: (skipWarning?: boolean) => void;
}

export const useLineItemCorrectionEdit = ({
  bills,
  setBills,
  lineItemSelections,
  onFullScreenDialogClose,
}: UseLineItemCorrectionEditProps) => {
  // Local states
  const [printData, setPrintData] = useState<PrintData | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDrucken, setIsDrucken] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [testToggleChecked, setTestToggleChecked] = useState(false);
  const [openTestTypeDialog, setOpenTestTypeDialog] = useState(false);
  const [testTypeData, setTestTypeData] = useState<TestTypeInterface>({
    type: TestType.GLOBAL,
    email: undefined,
  });
  const [isExportLoading, setIsExportLoading] = useState(false);
  const today = new Date();
  const todayString = today.toDateString();

  // State for Stepper
  const [billsChanged, setBillsChanged] = useState(false);
  const [billsSnapshot, setBillsSnapshot] = useState(JSON.stringify(bills));

  // If bills changes at any time, detect it
  useEffect(() => {
    const newSnapshot = JSON.stringify(bills);
    // If the snapshot differs from the last "saved" snapshot, mark as changed
    if (newSnapshot !== billsSnapshot) {
      setBillsChanged(true);
    }
  }, [bills, billsSnapshot]);

  // API wrappers from stores
  const TransactionStore = useStore(TransactionsContext);
  const {
    submitEditedBillingData,
    exportData,
    mergeDirectBillingDocuments,
  } = TransactionStore;

  const RxStore = useStore(RxStoreContext);
  const { updateIsTaxieren } = RxStore;

  // Batch print hook for LINE_ITEM_CORRECTION bills
  const {
    startBatchPrint,
    isLoading: isBatchPrinting,
    progress: batchProgress,
  } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.LineItemCorrection);

  // Initialize notes with data from bills
  useEffect(() => {
    const initialNotes = bills.reduce(
      (acc, bill) => {
        acc[bill.billId] = bill.notes || "";
        return acc;
      },
      {} as Record<string, string>,
    );
    setNotes(initialNotes);
  }, [bills]);

  const usePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforePrint: () => console.log("before print"),
    onAfterPrint: () => console.log("after print"),
  });

  // ----------------
  // Drucken (Print) Handler - Async Batch Workflow
  // ----------------
  const handleDrucken = async (testType: TestType) => {
    try {
      setIsDrucken(true);

      // Step 1: Pre-save all bills in parallel with selected line items
      const updatePromises = bills.map(async (bill) => {
        const { payerIK, accountInfo, data, billId, userInfo, wasExported } = bill;
        const { rxs, payerDto, totalPrice } = data;

        const rxsIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);
        const paperCenter = payerDto?.paperReceivingCenter?.ik;
        const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

        const dataSubmission: UpdateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: accountInfo.id,
          amount: totalPrice,
          userId: userInfo.id!,
          rxIds: rxsIds,
          paperCenterId: paperCenter || undefined,
          dataCenterId: digitalCenter || undefined,
          notes: notes[bill.billId] || "",
          wasExported: wasExported,
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
          // Include selected line items for position correction
          rxLineItemSelections: rxs
            .filter((rx) => rx.id)
            .map((rx) => ({
              rxId: rx.id!,
              selectedLineItems: (lineItemSelections[rx.id!] || []) as any,
            })),
        };

        // Pre-save transaction (update only, no PDF generation)
        await submitEditedBillingData(billId, dataSubmission);
      });

      await Promise.all(updatePromises);

      // Step 2: Queue batch PDF generation workflow
      const billIds = bills.map((bill) => bill.billId);
      const result = await startBatchPrint(billIds);
      const successfulResults = result?.results.filter((r) => r.success) ?? [];

      // Step 3: Extract doc IDs (no auto-download to match original behavior)
      if (successfulResults.length > 0) {
        const docIds = extractDocIdsFromBatchResults(successfulResults);
        // Merge PDFs without auto-download to preserve original behavior
        await mergeDirectBillingDocuments({
          paperDocumentIds: docIds.paperDocumentIds,
          billDocumentIds: docIds.billDocumentIds,
        });

        // Patch each bill's PDF links with the freshly generated document IDs
        // so the download buttons point at the new PDFs, not stale ones.
        setBills((prevBills) =>
          prevBills.map((bill) => {
            const jobResult = successfulResults.find((r) => r.entityId === bill.billId);
            const paperDocumentId = (jobResult as any)?.paperDocumentId;
            const billDocumentId = (jobResult as any)?.billDocumentId;
            if (!paperDocumentId && !billDocumentId) return bill;
            return {
              ...bill,
              ...(paperDocumentId ? { paperCenterReceipt: { id: paperDocumentId } as any } : {}),
              ...(billDocumentId ? { billPdf: { id: billDocumentId } as any } : {}),
            };
          }),
        );
      }

      // Show appropriate success/warning message
      if (result && result.failureCount > 0) {
        notificationStore.showMessage(
          `${result.successCount}/${result.totalCount} Position-Korrektur-Rechnungen erfolgreich erstellt. ${result.failureCount} Fehler.`,
          "warning",
        );
      } else {
        notificationStore.showMessage(
          "Position-Korrektur PDF wurde erfolgreich erstellt.",
          "success",
        );
      }

      setBillsChanged(false);
      setBillsSnapshot(JSON.stringify(bills));
    } catch (error: any) {
      let errorMessage = error.response?.data?.message || error.message;
      console.error("Error Printing Bills", error);
      notificationStore.showMessage(
        `Beim Drucken der Position-Korrektur ist ein Problem aufgetreten: ${errorMessage}`,
        "error",
        error,
      );
    } finally {
      setIsDrucken(false);
    }
  };

  // ----------------
  // Export Handler
  // ----------------
  const handleExport = () => {
    // Placeholder for export functionality
  };

  const handleExportConfirm = async (testOptions: TestTypeInterface) => {
    try {
      setIsExportLoading(true);
      // Implement export logic if needed
    } catch (error) {
      console.error("Export failed:", error);
      notificationStore.showMessage("Export fehlgeschlagen", "error");
    } finally {
      setIsExportLoading(false);
    }
  };

  return {
    // State
    bills,
    setBills,
    printData,
    setPrintData,
    isPrinting,
    setIsPrinting,
    isDrucken,
    printRef,
    notes,
    setNotes,
    testToggleChecked,
    setTestToggleChecked,
    openTestTypeDialog,
    setOpenTestTypeDialog,
    testTypeData,
    setTestTypeData,
    isExportLoading,
    billsChanged,
    setBillsChanged,
    billsSnapshot,
    setBillsSnapshot,

    // Batch print
    startBatchPrint,
    isBatchPrinting,
    batchProgress,

    // Handlers
    handleDrucken,
    handleExport,
    handleExportConfirm,
    usePrint,
  };
};
